import React, { useCallback } from 'react';
import Slider from 'react-slick';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@material-ui/core';
import PrintIcon from '@material-ui/icons/Print';
import avatarPlaceholder from 'assets/images/avatars/avatar-526x526.png';
import RMSIconButton from '../../../shared/components/buttons/RMSIconButton';
import { formatDisplayDate } from '../../../shared/utility/dateFormatter';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { showReport } from 'reducers/PersonDialogsReducer';
import { SLIDER_SETTINGS } from '../../constants/personInfoSummaryCardFormConstants';
import { resolveDescription, calculateAge } from '../../utility/personInfoSummaryCardHelper';

const Field = ({ label, value }) => (
  <Typography variant="body2" className="person-info-summary-card__line">
    <span className="person-info-summary-card__label">{label}:</span> {value || ''}
  </Typography>
);

const PersonInfoSummaryCard = ({
  person,
  photos = [],
  address,
  phone,
  email,
  ssn,
  oln,
  onOpenPhotoTab,
}) => {
  const dispatch = useDispatch();
  const { sexOptions, raceOptions, ethnicityOptions } = useUserDictionaryOptions();
  const ptsUserID = useSelector((state) => state.user?.userData?.user?.ptsUserID);
  const userCurrentClearanceLevel = useSelector(
    (state) => state.user?.userCurrentClearanceLevel?.Level
  );
  const userAgency = useSelector((state) => state.user?.userAgency);

  const handlePrint = useCallback(() => {
    dispatch(
      showReport({
        id: 250,
        title: 'Person History Report',
        data: {
          ptsPersonID: person?.ptsPersonID,
          AgencyID: userAgency,
          ClearanceLevel: parseInt(userCurrentClearanceLevel, 10) || '',
          userId: ptsUserID,
        },
      })
    );
  }, [dispatch, person, userAgency, userCurrentClearanceLevel, ptsUserID]);

  const handleOpenPhotoTab = useCallback(() => onOpenPhotoTab?.(), [onOpenPhotoTab]);

  if (!person) return null;

  return (
    <Box className="rms-dialog-section person-info-summary-card">
      <Box className="person-info-summary-card__photo" onClick={handleOpenPhotoTab}>
        {photos.length > 0 ? (
          <Slider {...SLIDER_SETTINGS} className="person-info-summary-card__slider">
            {photos.map((photo) => (
              <div key={photo.ptsImageID}>
                <img
                  alt={photo.FileName || 'Person'}
                  src={photo.Image ? `data:image/jpeg;base64,${photo.Image}` : avatarPlaceholder}
                  className="person-info-summary-card__photo-img"
                />
              </div>
            ))}
          </Slider>
        ) : (
          <img
            alt="Person"
            src={avatarPlaceholder}
            className="person-info-summary-card__photo-img"
          />
        )}
      </Box>

      <Box className="person-info-summary-card__col person-info-summary-card__col--identity">
        <Typography variant="subtitle1" className="person-info-summary-card__name">
          {person.PersonID ? `${person.PersonID} | ` : ''}
          {person.FullName}
        </Typography>
        <Field
          label="DOB"
          value={person.Born ? formatDisplayDate(person.Born) : ''}
        />
        <Box className="person-info-summary-card__row">
          <Field label="Age" value={calculateAge(person.Born)} />
          <Field label="Race" value={resolveDescription(raceOptions, person.Race)} />
        </Box>
        <Box className="person-info-summary-card__row">
          <Field label="Sex" value={resolveDescription(sexOptions, person.Sex)} />
          <Field label="Ethnicity" value={resolveDescription(ethnicityOptions, person.Ethnicity)} />
        </Box>
      </Box>

      <Box className="person-info-summary-card__col person-info-summary-card__col--ids">
        <Field label="SSN" value={ssn} />
        <Field label="OLN" value={oln} />
      </Box>

      <Box className="person-info-summary-card__col person-info-summary-card__col--contact">
        <Field label="Email" value={email} />
        <Field label="Phone" value={phone} />
        <Field label="Address" value={address} />
      </Box>

      <Box className="person-info-summary-card__print">
        <RMSIconButton
          icon={<PrintIcon />}
          tooltip="Print Person History Report"
          onClick={handlePrint}
        />
      </Box>
    </Box>
  );
};

export default PersonInfoSummaryCard;
